// The editor for Settings › Prices › consumer financing (BL-005): the lender, the apply link, and the
// plans, one row each (months, annual rate, smallest amount financed), added or removed. Saved as one
// setting by the row's own Save.
import type { FinancingPlan } from '@flowology/domain';
import { financingProblem, type FinancingSetting } from './financing-model.ts';

export function FinancingPlans({
  id,
  value,
  disabled,
  onChange,
}: {
  id: string;
  value: FinancingSetting;
  disabled: boolean;
  onChange: (v: FinancingSetting) => void;
}) {
  const set = (p: Partial<FinancingSetting>) => onChange({ ...value, ...p });
  const plan = (i: number, p: Partial<FinancingPlan>) =>
    set({ plans: value.plans.map((x, j) => (j === i ? { ...x, ...p } : x)) });
  const problem = financingProblem(value);
  return (
    <div className="financing-plans" role="group" aria-label="Consumer financing">
      <label>
        <span className="flabel">Lender</span>
        <input
          id={id}
          className="field"
          value={value.lender}
          disabled={disabled}
          onChange={(e) => set({ lender: e.target.value })}
        />
      </label>
      <label>
        <span className="flabel">Link a customer applies on (optional)</span>
        <input
          className="field"
          type="url"
          value={value.applyUrl ?? ''}
          placeholder="https://"
          disabled={disabled}
          onChange={(e) => set({ applyUrl: e.target.value.trim() || null })}
        />
      </label>
      {value.plans.map((p, i) => (
        <div key={i} className="plan-row" role="group" aria-label={`Plan ${i + 1}`}>
          <label>
            <span className="flabel">Months</span>
            <input
              className="field num"
              inputMode="numeric"
              value={String(p.months)}
              disabled={disabled}
              onChange={(e) => plan(i, { months: Number(e.target.value) })}
            />
          </label>
          <label>
            <span className="flabel">Annual rate %</span>
            <input
              className="field num"
              inputMode="decimal"
              value={String(p.aprPercent)}
              disabled={disabled}
              onChange={(e) => plan(i, { aprPercent: Number(e.target.value) })}
            />
          </label>
          <label>
            <span className="flabel">From $ (optional)</span>
            <input
              className="field num"
              inputMode="decimal"
              value={p.minCents ? String(p.minCents / 100) : ''}
              disabled={disabled}
              onChange={(e) => {
                const d = Number(e.target.value);
                const { minCents: _, ...rest } = p;
                const next: FinancingPlan = d > 0 ? { ...rest, minCents: Math.round(d * 100) } : rest;
                set({ plans: value.plans.map((x, j) => (j === i ? next : x)) });
              }}
            />
          </label>
          <button
            type="button"
            className="btn quiet"
            disabled={disabled}
            onClick={() => set({ plans: value.plans.filter((_, j) => j !== i) })}
          >
            Remove plan
          </button>
        </div>
      ))}
      <button
        type="button"
        className="btn soft"
        disabled={disabled || value.plans.length >= 12}
        onClick={() => set({ plans: [...value.plans, { months: 60, aprPercent: 9.99 }] })}
      >
        Add a plan
      </button>
      {problem && (
        <p className="small bad-ink" role="alert">
          {problem}
        </p>
      )}
    </div>
  );
}
